import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, inject, injectable } from '@evertel/di'
import moment from 'moment'
import _ from 'lodash'
import debugModule from 'debug'

// evertel
import { LocalAsyncStorage } from '@evertel/utils'
import { PollConfig } from '@evertel/session/PollConfig'

const debug = debugModule('app:IdleTimerService')

// **********************************************
// This singleton tracks and auto-updates the
// idle time for the current user. No need to
// update the timer manually.
// **********************************************

const ONE_MIN_IN_MS = 60 * 1000
const USER_ACTIVITY_TIMEOUT_POLL_INTERVAL_MS = ONE_MIN_IN_MS // every 1 min
const DEFAULT_TIMEOUT_MINUTES = 30 // CJIS-compliant default
const STORAGE_KEY = 'sessionTimeoutMinutes'
const LAST_ACTIVITY_STORAGE_KEY = 'lastActivityTimestamp'

class IdleTimerService {
    lastActivity = moment()
    currentTime = moment()
    isAppActive = true
    timeoutMinutes: number | null = DEFAULT_TIMEOUT_MINUTES

    private idleTimerPollInterval: NodeJS.Timeout | null = null // holds the setInterval timer

    onIdleCallback?: () => void

    constructor(
        private localStorage: LocalAsyncStorage
    ) {
        makeAutoObservable(this)
    }

    // onIdleCallback is called when the user has been idle for the specified amount of time above. in web its used to call logoutService.logout()
    // if we reference LogoutService directly, we would have a circular dependency
    setOnIdleCallback(onIdleCallback: () => void) {
        this.onIdleCallback = onIdleCallback
    }

    init = async () => {
        await this.loadTimeoutSetting()
        this.lastActivity = moment()
        await this.persistLastActivity()

        this.start()

        document.addEventListener('visibilitychange', this.onVisibilityChange)
        document.addEventListener('pointerdown', this.reset)
        document.addEventListener('keydown', this.debouncedReset)
        document.addEventListener('scroll', this.debouncedReset, { passive: true, capture: true })
        window.addEventListener('blur', this.onWindowBlur)
        window.addEventListener('focus', this.onWindowFocus)
    }

    private async loadTimeoutSetting() {
        const stored = await this.localStorage.getItem(STORAGE_KEY)
        if (stored !== null) {
            runInAction(() => {
                // stored value of 'null' (string) means disabled
                const value = stored === 'null' ? null : Number(stored)
                this.timeoutMinutes = Number.isNaN(value) ? null : value
            })
        }
    }

    private async persistLastActivity() {
        await this.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, this.lastActivity.toISOString())
    }

    async setTimeoutMinutes(minutes: number | null) {
        runInAction(() => {
            this.timeoutMinutes = minutes
        })
        await this.localStorage.setItem(STORAGE_KEY, String(minutes))
    }

    async clearTimeoutSetting() {
        runInAction(() => {
            this.timeoutMinutes = DEFAULT_TIMEOUT_MINUTES
        })
        await this.localStorage.removeItem(STORAGE_KEY)
    }

    onVisibilityChange = () => {
        debug('App Visibility:', document.visibilityState)

        this.isAppActive = !document.hidden

        if (!document.hidden) {
            this.reset()
        }
    }

    onWindowBlur = () => {
        // debug('Window lost focus')
        this.isAppActive = false
    }

    onWindowFocus = () => {
        // debug('Window gained focus')
        this.isAppActive = true
        this.reset()
    }

    start = () => {
        this.stop()
        if (!PollConfig.globalEnabled) return

        this.idleTimerPollInterval = setInterval(() => {
            runInAction(() => { this.currentTime = moment() })
            debug('Idle Timer - Time Inactive:', this.idleTimerMinutesInactive)

            // null means disabled
            if (this.timeoutMinutes !== null && this.idleTimerMinutesInactive >= this.timeoutMinutes) {
                this.destroyTimers()
                this.clearTimeoutSetting()
                if (this.onIdleCallback) this.onIdleCallback()
            }
        }, USER_ACTIVITY_TIMEOUT_POLL_INTERVAL_MS)

    }

    stop = () => {
        if (this.idleTimerPollInterval) clearInterval(this.idleTimerPollInterval)
    }

    reset = () => {
        debug('Idle Timer - reset activity')
        if (this.isAppActive) {
            this.lastActivity = moment()
            this.persistLastActivity()
        }
    }

    readonly debouncedReset = _.debounce(this.reset, 500, {
        leading: false,
        trailing: true
    })

    destroyTimers = () => {
        this.stop()
        document.removeEventListener('pointerdown', this.reset)
        document.removeEventListener('keydown', this.debouncedReset)
        document.removeEventListener('scroll', this.debouncedReset, { capture: true })
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        window.removeEventListener('blur', this.onWindowBlur)
        window.removeEventListener('focus', this.onWindowFocus)
    }

    get idleTimerMinutesInactive() {
        return this.currentTime.diff(this.lastActivity, 'seconds') / 60
    }
}

decorate(injectable(), IdleTimerService)
decorate(inject(LocalAsyncStorage), IdleTimerService, 0)

export { IdleTimerService, LAST_ACTIVITY_STORAGE_KEY }
